import * as jspb from "google-protobuf";

import { IH264VideoFrame } from "@formant/core/model/IH264VideoFrame";

import {
    IBinaryChunkMessage,
    IBinaryH264Message,
} from "../models/IBinaryH264Message";

/**
 * Detects if a message is a binary H264 message by checking for the "H264" magic bytes
 */
export function isBinaryH264Message(data: ArrayBuffer): boolean {
    if (data.byteLength < 4) {
        return false;
    }
    const view = new Uint8Array(data);
    return (
        view[0] === 0x48 && // 'H'
        view[1] === 0x32 && // '2'
        view[2] === 0x36 && // '6'
        view[3] === 0x34 // '4'
    );
}
/**
 * Detects if a message is a binary chunk by checking for the "BCHK" magic bytes
 */
export function isBinaryChunkMessage(data: ArrayBuffer): boolean {
    if (data.byteLength < 4) {
        return false;
    }
    const view = new Uint8Array(data);
    return (
        view[0] === 0x42 && // 'B'
        view[1] === 0x43 && // 'C'
        view[2] === 0x48 && // 'H'
        view[3] === 0x4b // 'K'
    );
}

/**
 * Parses a binary H264 message according to the Go agent's format:
 * [4 bytes: "H264"] + [8 bytes: timestamp] + [2 bytes: stream name length] + [n bytes: stream name] + [m bytes: protobuf data]
 */
export function parseBinaryH264Message(data: ArrayBuffer): IBinaryH264Message {
    if (!isBinaryH264Message(data)) {
        throw new Error("Data is not a binary H264 message");
    }

    const view = new DataView(data);

    // Extract timestamp (8 bytes, big endian, starting at offset 4)
    const timestamp = Number(view.getBigUint64(4, false));

    // Extract stream name length (2 bytes, big endian, starting at offset 12)
    const streamNameLength = view.getUint16(12, false);

    // Extract stream name (starting at offset 14)
    const streamNameBytes = new Uint8Array(data, 14, streamNameLength);
    const streamName = new TextDecoder().decode(streamNameBytes);

    // Extract protobuf data (everything after the header + stream name)
    const protobufOffset = 14 + streamNameLength;
    const protobufData = data.slice(protobufOffset);

    // Parse the protobuf data using protobufjs library
    const frame = parseH264VideoFrameProtobuf(protobufData);

    return {
        communicationType: "binary-h264",
        timestamp,
        streamName,
        frame,
    };
}

/**
 * Parses a binary chunk message according to the Go agent's format:
 * [4 bytes: "BCHK"] + [4 bytes: chunk_id] + [4 bytes: seq] + [4 bytes: total] + [n bytes: data]
 */
export function parseBinaryChunkMessage(
    data: ArrayBuffer,
): IBinaryChunkMessage {
    if (!isBinaryChunkMessage(data)) {
        throw new Error("Data is not a binary chunk message");
    }

    const view = new DataView(data);

    // Extract chunk metadata (all big endian)
    const chunkId = view.getUint32(4, false);
    const sequence = view.getUint32(8, false);
    const total = view.getUint32(12, false);

    // Extract chunk data
    const chunkData = new Uint8Array(data.slice(16));

    return {
        communicationType: "binary-chunk",
        chunkId,
        sequence,
        total,
        data: chunkData,
    };
}

/**
 * Proper protobuf parser for H264VideoFrame using google-protobuf
 * This replaces the manual parser with the project's standard protobuf library
 */
function parseH264VideoFrameProtobuf(data: ArrayBuffer): IH264VideoFrame {
    const reader = new jspb.BinaryReader(new Uint8Array(data));

    let index = 0;
    let flags = 0;
    let frameData = "";

    try {
        while (reader.nextField()) {
            if (reader.isEndGroup()) {
                break;
            }
            const fieldNumber = reader.getFieldNumber();

            switch (fieldNumber) {
                case 1: // index field (int32)
                    index = reader.readInt32();
                    break;
                case 2: // flags field (int32)
                    flags = reader.readInt32();
                    break;
                case 3: {
                    // frame_data field (bytes)
                    const frameBytes = reader.readBytes();
                    frameData = btoa(String.fromCharCode(...frameBytes));
                    break;
                }
                default:
                    reader.skipField();
                    break;
            }
        }

        return {
            index,
            flags,
            frame_data: frameData,
        };
    } catch (error) {
        throw new Error(`Failed to decode H264VideoFrame: ${error}`);
    }
}
